
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Button,
  InputField,
  TextAreaField,
  CircularLoader,
  NoticeBox, Tooltip, SingleSelect, SingleSelectOption
} from '@dhis2/ui';
import { useDataEngine } from '@dhis2/app-runtime';
import { useAuth } from './../auth/AuthContext';
import { POSITION_ATTRIBUTES } from '../staff/api/constants';
import { DATA_ELEMENTS } from './api/appraisalConstants';
import { OBJECTIVE_APPRAISAL_DE, APPRAISAL_STATUS_CODES } from './api/appraisalConstants';
import {
  ChevronRight, TrendingUp, Star, CheckCircle, Clock,
  Lock, Users, MessageCircleMoreIcon, Eye, ThumbsUp, ThumbsDown
} from 'lucide-react';
import './AppraisalForm.css';import { GiCheckMark } from 'react-icons/gi';

import { useRatingScale } from './hooks/useRatingScale';  
import { createRatingComponents } from './components/RatingComponents'; 
import { useWeightConfig } from './hooks/useWeightConfig';

import { useLeadershipCompetencies } from './hooks/useLeadershipCompetencies';


// Objective Data Elements (from your PerformanceList)
const OBJECTIVE_DATA_ELEMENTS = {
  NAME: 'EuN5ztFnXsQ',
  DESCRIPTION: 'nWqW5BrcgP1',
  START_DATE: 'eDcVy661j9e',
  COMPLETION_DATE: 'J0rjxrDKn1l',
  APPROVAL_STATUS: 'DzQorireRBf',
};

// ─── Progress Bar ────────────────────────────────────────────────────────────
const ProgressBar = ({ value = 0 }) => {
  const fillClass =
    value >= 80 ? 'apr-progress-fill--high'
      : value >= 50 ? 'apr-progress-fill--mid'
        : 'apr-progress-fill--low';
  return (
    <div className="apr-progress-wrap">
      <div className="apr-progress-track">
        <div
          className={`apr-progress-fill ${fillClass}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <span className="apr-progress-pct">{value}%</span>
    </div>
  );
};

// ─── Status Chip ─────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    approved: { label: 'Approved', cls: 'apr-chip--approved', icon: <CheckCircle size={11} /> },
    pending: { label: 'Pending', cls: 'apr-chip--pending', icon: <Clock size={11} /> },
    draft: { label: 'Draft', cls: 'apr-chip--draft', icon: null },
    submitted: { label: 'Submitted', cls: 'apr-chip--submitted', icon: <Clock size={11} /> },
    reviewed: { label: 'Reviewed', cls: 'apr-chip--reviewed', icon: <Star size={11} /> },
    finalized: { label: 'Finalized', cls: 'apr-chip--finalized', icon: <CheckCircle size={11} /> },
  };
  const cfg = map[status] || map['pending'];
  return (
    <span className={`apr-chip ${cfg.cls}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

const RATING_DEFINITIONS = {
  '5': { 
    label: 'Outstanding', 
    description: 'Consistently exceeds expectations and demonstrates exceptional performance. Sets new standards and serves as a role model.',
    color: '#28a745',
    shortDesc: 'Exceptional performance'
  },
  '4': { 
    label: 'Exceeds Expectations', 
    description: 'Often exceeds targets and requirements. Shows initiative and consistently delivers high-quality results.',
    color: '#20d429',
    shortDesc: 'Above standard'
  },
  '3': { 
    label: 'Meets Expectations', 
    description: 'Consistently meets all job requirements and objectives. Demonstrates solid, reliable performance.',
    color: '#ffc107',
    shortDesc: 'Meets standard'
  },
  '2': { 
    label: 'Needs Improvement', 
    description: 'Sometimes falls short of requirements. Requires guidance and development in certain areas.',
    color: '#fd7e14',
    shortDesc: 'Below standard'
  },
  '1': { 
    label: 'Unsatisfactory', 
    description: 'Does not meet minimum requirements. Significant improvement needed or performance improvement plan required.',
    color: '#dc3545',
    shortDesc: 'Fails to meet standard'
  },
  '0': { 
    label: 'Not Rated', 
    description: 'No rating provided yet.',
    color: '#6c757d',
    shortDesc: 'Not evaluated'
  }
};

// ─── Workflow Stepper ─────────────────────────────────────────────────────────
const STEPS = [
  { key: APPRAISAL_STATUS_CODES.DRAFT, label: 'Draft', num: 1 },
  { key: APPRAISAL_STATUS_CODES.SUBMITTED, label: 'Submitted', num: 2 },
  { key: APPRAISAL_STATUS_CODES.MANAGER_REVIEWED, label: 'Manager Review', num: 3 },
  { key: APPRAISAL_STATUS_CODES.FINALIZED, label: 'Finalized', num: 4 },
];

const STATUS_HINTS = {
  [APPRAISAL_STATUS_CODES.DRAFT]: 'You can edit and save as draft or submit for manager review.',
  [APPRAISAL_STATUS_CODES.SUBMITTED]: 'Awaiting manager review. Manager will provide their rating and comments.',
  [APPRAISAL_STATUS_CODES.MANAGER_REVIEWED]: 'Manager has provided their rating. Please review and finalize.',
  [APPRAISAL_STATUS_CODES.FINALIZED]: 'Appraisal is complete and locked.',
};

const RatingCardSelector = ({ options, selected, onChange, disabled, ratingScale }) => {
  return (
    <div className="rating-card-selector">
      {options.map((opt) => {
        const isSelected = selected === opt.value;
        const scaleColor = ratingScale?.[opt.value]?.color;
        const scaleBg = ratingScale?.[opt.value]?.bgColor;

        // Build inline styles for the selected state using the rating scale colors
        const cardStyle = {
          borderColor: isSelected ? (scaleColor || '#006b9e') : '#d0d7de',
          backgroundColor: isSelected ? (scaleBg || '#e6f2ff') : '#ffffff',
          color: isSelected ? (scaleColor || '#006b9e') : '#1e293b',
        };

        let cardClass = 'rating-card';
        if (isSelected) cardClass += ' rating-card--selected';
        if (disabled) cardClass += ' rating-card--disabled';

        return (
          <button
            key={opt.value}
            type="button"
            className={cardClass}
            style={cardStyle} // inline styles override the CSS class for selected state
            onClick={() => onChange(opt.value)}
            disabled={disabled}
          >
            <span style={{ fontWeight: 700, marginRight: '4px' }}>{opt.value}</span>
            – {opt.label}
          </button>
        );
      })}
    </div>
  );
};

const WorkflowStepper = ({ currentStatus, isTopLevelUser }) => {
  // For top-level users, simplify the workflow
  if (isTopLevelUser) {
    return (
      <div className="apr-stepper">
        <div key="self-rating" className="apr-step">
          <div className="apr-step-circle apr-step-circle--done">
            <CheckCircle size={12} />
          </div>
          <span className="apr-step-label apr-step-label--done">Self Rating</span>
        </div>
        <div key="auto-approved" className="apr-step">
          <div className="apr-step-circle apr-step-circle--done">
            <CheckCircle size={12} />
          </div>
          <span className="apr-step-label apr-step-label--done">Auto-Approved</span>
        </div>
        <div className="apr-step-connector apr-step-connector--active" style={{ width: '100%', left: 0, top: '26px', position: 'absolute' }} />
      </div>
    );
  }

  const stepOrder = [
    APPRAISAL_STATUS_CODES.DRAFT,
    APPRAISAL_STATUS_CODES.SUBMITTED,
    APPRAISAL_STATUS_CODES.MANAGER_REVIEWED,
    APPRAISAL_STATUS_CODES.FINALIZED,
  ];

  const currentIdx = stepOrder.indexOf(currentStatus);

  return (
    <div className="apr-stepper">
      <div 
        className="apr-step-connector" 
        style={{ 
          width: 'calc(100% - 100px)', 
          left: '50px', 
          top: '26px', 
          position: 'absolute',
          background: `linear-gradient(to right, var(--apr-success) ${(currentIdx / (STEPS.length - 1)) * 100}%, var(--apr-border-light) ${(currentIdx / (STEPS.length - 1)) * 100}%)`
        }} 
      />
      {STEPS.map((step, idx) => {
        const isDone = idx < currentIdx;
        const isActive = step.key === currentStatus;

        let circleClass = '';
        let labelClass = '';
        if (isDone) { circleClass = 'apr-step-circle--done'; labelClass = 'apr-step-label--done'; }
        else if (isActive) { circleClass = 'apr-step-circle--active'; labelClass = 'apr-step-label--active'; }

        return (
          <div className="apr-step" key={step.key}>
            <div className={`apr-step-circle ${circleClass}`}>
              {isDone ? <CheckCircle size={12} /> : step.num}
            </div>
            <span className={`apr-step-label ${labelClass}`}>
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Employee Response Summary (Read-only display) ─────────────────────────────
const EmployeeResponseSummary = ({ 
  responseStatus, 
  responseRemarks, 
  acceptedTaskRating, 
  acceptedBehaviouralRating,
  managerRatingTask,
  managerRatingBehavioural
}) => {
  if (!responseStatus) return null;
  
  const isAccepted = responseStatus === 'accepted';
  
  return (
    <div className="apr-card" style={{ marginTop: '1rem', backgroundColor: '#f8f9fa' }}>
      <div className="apr-card-header">
        <div className="apr-card-header-left">
          <span className="apr-card-icon"><MessageCircleMoreIcon size={17} /></span>
          <h4 className="apr-card-title">Employee's Response to Manager's Rating</h4>
        </div>
      </div>
      
      <div style={{ padding: '1rem' }}>
        {/* Response Status */}
        <div style={{
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '1rem',
          backgroundColor: isAccepted ? '#d4edda' : '#f8d7da',
          borderLeft: `4px solid ${isAccepted ? '#28a745' : '#dc3545'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isAccepted ? (
              <ThumbsUp size={20} color="#28a745" />
            ) : (
              <ThumbsDown size={20} color="#dc3545" />
            )}
            <strong style={{ color: isAccepted ? '#155724' : '#721c24' }}>
              Employee {isAccepted ? 'ACCEPTED' : 'DISPUTED'} the Manager's Rating
            </strong>
          </div>
        </div>
        
        {/* Comparison of Ratings */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
            <strong style={{ color: '#1565c0' }}>Manager's Recommended Rating:</strong>
            <div style={{ marginTop: '8px' }}>
              <div>Business Objectives: <strong>{managerRatingTask || 'Not rated'}</strong></div>
              <div>Behavioural Criteria: <strong>{managerRatingBehavioural || 'Not rated'}</strong></div>
              <div>Total Score: <strong>{((safeParse(managerRatingTask) + safeParse(managerRatingBehavioural)) / 2).toFixed(2)}</strong></div>
            </div>
          </div>
          
          {isAccepted && (
            <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #28a745' }}>
              <strong style={{ color: '#28a745' }}>Employee's Accepted Rating:</strong>
              <div style={{ marginTop: '8px' }}>
                <div>Business Objectives: <strong>{acceptedTaskRating || 'Not rated'}</strong></div>
                <div>Behavioural Criteria: <strong>{acceptedBehaviouralRating || 'Not rated'}</strong></div>
                <div>Total Score: <strong>{((safeParse(acceptedTaskRating) + safeParse(acceptedBehaviouralRating)) / 2).toFixed(2)}</strong></div>
              </div>
            </div>
          )}
        </div>
        
        {/* Employee Remarks */}
        {responseRemarks && (
          <div style={{
            padding: '12px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <strong>Employee's Remarks:</strong>
            <p style={{ marginTop: '8px', marginBottom: 0, lineHeight: 1.5, fontStyle: 'italic' }}>
              "{responseRemarks}"
            </p>
          </div>
        )}
      </div>
    </div>
  );
};


const EmployeeResponseSection = ({
  onFinalize,
  disabled,
  managerRatingTask,
  managerRatingBehavioural,
  managerComments,
  initialRemarks = ''
}) => {
  const [remarks, setRemarks] = useState(initialRemarks);
  const [agrees, setAgrees] = useState(true);

  return (
    <div className="apr-section">
      <div className="apr-card">
        <div className="apr-card-header">
          <div className="apr-card-header-left">
            <span className="apr-card-icon"><MessageCircleMoreIcon size={20} /></span>
            <h4 className="apr-card-title">Manager's Evaluation Review</h4>
          </div>
        </div>
        <div className="apr-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)',
            padding: '1.25rem',
            borderRadius: '10px',
            marginBottom: '1.5rem',
            border: '1px solid #bbdefb'
          }}>
            <h5 style={{ margin: 0, color: '#1565c0', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontWeight: 800 }}>
              <Eye size={16} /> Manager's Recommended Rating
            </h5>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginTop: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#546e7a', fontWeight: 700, textTransform: 'uppercase' }}>Business Objectives</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{managerRatingTask || 'Not rated'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#546e7a', fontWeight: 700, textTransform: 'uppercase' }}>Behavioural Criteria</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{managerRatingBehavioural || 'Not rated'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#546e7a', fontWeight: 700, textTransform: 'uppercase' }}>Total Score</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1565c0' }}>{((safeParse(managerRatingTask) + safeParse(managerRatingBehavioural)) / 2).toFixed(2)}</div>
              </div>
            </div>
            {managerComments && (
              <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #bbdefb', fontStyle: 'italic', color: '#37474f' }}>
                "<strong>Manager Comments:</strong> {managerComments}"
              </div>
            )}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ fontWeight: 800, marginBottom: '1rem', display: 'block', fontSize: '1.1rem' }}>
              Do you agree with the manager's rating?
            </label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="button"
                onClick={() => setAgrees(true)}
                className="apr-btn"
                style={{
                  flex: 1,
                  background: agrees ? 'var(--apr-success)' : '#fff',
                  color: agrees ? '#fff' : 'var(--apr-text)',
                  border: `2px solid ${agrees ? 'var(--apr-success)' : 'var(--apr-border)'}`,
                  padding: '1rem'
                }}
              >
                ✓ I Agree
              </button>
              <button
                type="button"
                onClick={() => setAgrees(false)}
                className="apr-btn"
                style={{
                  flex: 1,
                  background: !agrees ? 'var(--apr-danger)' : '#fff',
                  color: !agrees ? '#fff' : 'var(--apr-text)',
                  border: `2px solid ${!agrees ? 'var(--apr-danger)' : 'var(--apr-border)'}`,
                  padding: '1rem'
                }}
              >
                ✗ I Disagree
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <TextAreaField
              label={agrees ? "Employee Remarks" : "Reason for Disagreement"}
              value={remarks}
              onChange={({ value }) => setRemarks(value)}
              rows={4}
              placeholder={agrees ? 'Provide any final comments...' : 'Please explain why you disagree...'}
              inputWidth="100%"
              disabled={disabled}
              required
            />
          </div>

          <button
            className="apr-btn apr-btn--success"
            style={{ width: '100%', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', fontSize: '1rem' }}
            onClick={() => remarks.trim() && onFinalize(remarks, agrees)}
            disabled={!remarks.trim() || disabled}
          >
            <GiCheckMark size={20} /> Finalize Appraisal
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function
const safeParse = (str) => {
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

const getProgressColor = (progress) => {
  if (progress >= 100) return '#28a745';
  if (progress >= 50) return '#ffc107';
  return '#dc3545';
};

// ─── Score Row ────────────────────────────────────────────────────────────────
const ScoreRow = ({ label, hint, value, onChange, weight = '50', disabled = false, score = '' }) => (
  <div className="apr-score-row">
    <div className="apr-score-cell-main">
      <div className="apr-score-label">{label}</div>
      {hint && <div className="apr-score-hint">{hint}</div>}
    </div>
    <div className="apr-score-cell-rating">
      <InputField
        disabled={disabled}
        type="number"
        value={String(value ?? '')}
        onChange={({ value }) => onChange(value)}
        min="0"
        max="5"
        step="1"
        inputWidth="100%"
      />
    </div>
    <div className="apr-score-cell-weight">{weight}%</div>
    <div className="apr-score-cell-result">{score}</div>
  </div>
);

// ─── Objective Row ────────────────────────────────────────────────────────────
const ObjectiveRow = React.memo(({
  obj,
  isExpanded,
  onToggle,
  savingObjectiveId,
  onRatingChange,
  onCommentsChange,
  disabled = false
}) => {
  const selfRating =
    obj.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.SELF_RATING)?.value || '';
  const selfComments =
    obj.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS)?.value || '';

  return (
    <div className={`apr-objective-card ${isExpanded ? 'apr-objective-card--expanded' : ''}`}>
      <div
        className="apr-obj-row"
        onClick={onToggle}
      >
        <div className="apr-obj-name">
          <ChevronRight 
            size={18} 
            className={`apr-obj-expand-icon ${isExpanded ? 'apr-obj-expand-icon--open' : ''}`}
            style={{ color: isExpanded ? 'var(--apr-primary)' : 'var(--apr-text-muted)' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '1rem', fontWeight: 700 }}>{obj.name}</span>
            {obj.description && (
              <span style={{ fontSize: '0.8rem', color: 'var(--apr-text-secondary)', fontWeight: 400 }}>
                {obj.description.length > 80 ? `${obj.description.substring(0, 80)}...` : obj.description}
              </span>
            )}
          </div>
        </div>
        <div>
          <ProgressBar value={obj.progress} />
        </div>
        <div style={{ textAlign: 'center', fontSize: '0.85rem', fontWeight: 600, color: 'var(--apr-text-secondary)' }}>
          {obj.completedKpis || 0}/{obj.kpiCount || 0} Done
        </div>
        <div style={{ textAlign: 'center' }}>
          <StatusChip status={obj.approvalStatus || 'pending'} />
        </div>
        <div style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--apr-text-muted)', fontWeight: 500 }}>
          {obj.completionDate || '—'}
        </div>
      </div>

      {isExpanded && (
        <div className="apr-obj-panel">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div>
              <h5 className="apr-obj-panel-title">
                <Star size={16} /> Rate This Objective
              </h5>
              <div className="apr-rating-row">
                <span className="apr-rating-label">Overall Rating (0 – 5):</span>
                <div style={{ width: 120 }}>
                  <InputField
                    disabled={disabled}
                    type="number"
                    value={selfRating}
                    onChange={({ value }) => {
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        onRatingChange(value);
                      }
                    }}
                    min="0"
                    max="5"
                    step="1"
                    inputWidth="100%"
                  />
                </div>
              </div>
              <TextAreaField
                label="Self Comments"
                value={selfComments}
                rows={3}
                inputWidth="100%"
                disabled={disabled}
                onChange={({ value }) => onCommentsChange(value)}
                placeholder="Describe your achievement..."
              />
              {savingObjectiveId === obj.id && (
                <div className="apr-saving-indicator">
                  <CircularLoader small />
                  <span>Auto-saving...</span>
                </div>
              )}
            </div>

            {obj.kpis && obj.kpis.length > 0 && (
              <div>
                <h5 className="apr-obj-panel-title" style={{ color: 'var(--apr-text-secondary)' }}>
                  📊 KPI Details
                </h5>
                <div style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>
                  {obj.kpis.filter(k => k.name).map((kpi, idx) => (
                    <div key={idx} style={{ 
                      marginBottom: '10px', 
                      padding: '12px', 
                      backgroundColor: '#fff', 
                      borderRadius: '8px',
                      border: '1px solid var(--apr-border-light)',
                      boxShadow: 'var(--apr-shadow-sm)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>{kpi.name}</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--apr-text-muted)' }}>Target: {kpi.target}</span>
                      </div>
                      <ProgressBar value={kpi.progress} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────────
const AppraisalForm = ({
  positionTEI,
  programStage,
  orgUnit,
  readOnlyReport = false,
  isTopLevelUser = false,
  approvalHierarchy = { hasApprover: true },
  appraisalEvent = null,
  fiscalYearStart = '',
  fiscalYearEnd = '',
  selectedFiscalYear = null,
  availableFiscalYears = [],
}) => {

  const { weightConfig, loading: weightConfigLoading } = useWeightConfig(orgUnit);
  
  // Get weights with validation
  const businessWeight = weightConfig?.businessObjectives?.weight || 50;
  const leadershipWeight = weightConfig?.leadershipBehavioural?.weight || 50;
  
  // Validate total is 100 (if not, adjust temporarily for display)
  const validBusinessWeight = businessWeight + leadershipWeight === 100 ? businessWeight : 50;
  const validLeadershipWeight = businessWeight + leadershipWeight === 100 ? leadershipWeight : 50;

  const { 
    competencies: leadershipCompetencies, 
    loading: leadershipLoading 
  } = useLeadershipCompetencies(orgUnit);

  // State for individual competency ratings
  const [competencyRatings, setCompetencyRatings] = useState({});
  const [competencyComments, setCompetencyComments] = useState({});
  

  // Update the calculateTotalScore function to use dynamic weights
  const calculateTotalScore = useCallback((taskRating, behaviouralRating) => {
    const task = safeParse(taskRating);
    const behavioural = safeParse(behaviouralRating);
    const businessPortion = (task * (validBusinessWeight / 100));
    const leadershipPortion = (behavioural * (validLeadershipWeight / 100));
    return (businessPortion + leadershipPortion).toFixed(2);
  }, [validBusinessWeight, validLeadershipWeight]);

  const normalizeTei = (input) => {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object' && input !== null) {
      return input.tei || input.trackedEntityInstance || input.id || input.value || '';
    }
    return String(input).trim();
  };
  
    const positionTeiString = normalizeTei(positionTEI);

  const engine = useDataEngine();
  const { user } = useAuth();

  const [appraisalData, setAppraisalData] = useState({
    employeeSelfRatingTask: '',
    employeeSelfRatingBehavioural: '',
    employeeSelfComments: '',
    managerRatingTask: '',
    managerRatingBehavioural: '',
    managerComments: '',
    managerBusinessComments: '',
    appraisalStatus: APPRAISAL_STATUS_CODES.DRAFT,
    eventId: null,
    employeeCompetencyRatings: '',
    employeeCompetencyComments: '',
    managerCompetencyRatings: '',
    managerCompetencyComments: '',
  });

  const [recentObjectives, setRecentObjectives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedObjectiveId, setExpandedObjectiveId] = useState(null);
  const [savingObjectiveId, setSavingObjectiveId] = useState(null);
  const [isEmployee, setIsEmployee] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [roleCheckComplete, setRoleCheckComplete] = useState(false);
  const [incumbentTei, setIncumbentTei] = useState(null);
  const [managerCanView, setManagerCanView] = useState(false);

  const [employeeResponseRemarks, setEmployeeResponseRemarks] = useState('');

  const [employeeResponseStatus, setEmployeeResponseStatus] = useState('');
  const [employeeAcceptedRatingTask, setEmployeeAcceptedRatingTask] = useState('');
  const [employeeAcceptedRatingBehavioural, setEmployeeAcceptedRatingBehavioural] = useState('');

  const [managerCompetencyRatings, setManagerCompetencyRatings] = useState({});
  const [managerCompetencyComments, setManagerCompetencyComments] = useState({});
  const [expandedCompId, setExpandedCompId] = useState(null);

  // Load business rating scale (for task/objectives)
  const { ratingScale: businessRatingScale, loading: businessScaleLoading } = useRatingScale(orgUnit, 'business');
  
  // Load leadership rating scale (for behavioural/leadership)
  const { ratingScale: leadershipRatingScale, loading: leadershipScaleLoading } = useRatingScale(orgUnit, 'leadership');
  
  // Create rating components with the loaded definitions
  const { EnhancedScoreRow: BusinessScoreRow, CriteriaSection } = useMemo(() => 
    createRatingComponents(businessRatingScale), 
    [businessRatingScale]
  );
  
  const { EnhancedScoreRow: LeadershipScoreRow } = useMemo(() => 
      createRatingComponents(leadershipRatingScale), 
      [leadershipRatingScale]
    );

  const isCurrentUserManager = useMemo(() => {
      return isManager && !isEmployee;
    }, [isManager, isEmployee]);

  const effectiveStart = selectedFiscalYear
    ? availableFiscalYears.find(y => y.year === selectedFiscalYear)?.start
    : fiscalYearStart;

  const effectiveEnd = selectedFiscalYear
    ? availableFiscalYears.find(y => y.year === selectedFiscalYear)?.end
    : fiscalYearEnd;

   
  const saveObjectiveRating = async (objectiveId, dataElement, value) => {
    setSavingObjectiveId(objectiveId);
    try {
      const objective = recentObjectivesRef.current.find(obj => obj.id === objectiveId);
      if (!objective) throw new Error('Objective not found');
      const updatedDataValues = [...(objective.dataValues || [])];
      const idx = updatedDataValues.findIndex(dv => dv.dataElement === dataElement);
      if (idx >= 0) {
        updatedDataValues[idx] = { dataElement, value };
      } else {
        updatedDataValues.push({ dataElement, value });
      }

      const fullEventResponse = await engine.query({
        event: {
          resource: `events/${objectiveId}`,
          params: { fields: 'event,program,programStage,orgUnit,eventDate,status,dataValues[dataElement,value]' }
        }
      });

      if (!fullEventResponse.event) {
        throw new Error(`Objective event ${objectiveId} not found`);
      }

      const fullEvent = fullEventResponse.event;
      if (!fullEvent) throw new Error('Objective event not found');

      await engine.mutate({
        type: 'update',
        resource: `events/${objectiveId}`,
        data: {
          ...fullEvent,
          dataValues: updatedDataValues,
          program: 'yiT00XKrrHP',
          programStage: 'VIBtpvkpHFR',
          status: 'ACTIVE'
        },
        headers: { 'Content-Type': 'application/json' }
      });

      setRecentObjectives(prev =>
        prev.map(obj =>
          obj.id === objectiveId
            ? { ...obj, dataValues: updatedDataValues }
            : obj
        )
      );

    } catch (err) {
      console.error('Save rating failed:', err);
      alert(`Failed to save rating: ${err.message}`);
    } finally {
      setSavingObjectiveId(null);
    }
  };

  
 
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 AppraisalData Debug:', {
        positionTeiString,
        eventId: appraisalData.eventId,
        status: appraisalData.appraisalStatus,
        employeeRatings: {
          task: appraisalData.employeeSelfRatingTask,
          behavioural: appraisalData.employeeSelfRatingBehavioural,
          comments: appraisalData.employeeSelfComments
        },
        managerRatings: {
          task: appraisalData.managerRatingTask,
          behavioural: appraisalData.managerRatingBehavioural,
          comments: appraisalData.managerComments
        }
      });
    }
  }, [positionTeiString, appraisalData]);

  useEffect(() => {
    recentObjectivesRef.current = recentObjectives;
  }, [recentObjectives]);

  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      debounceTimeouts.current = {};
    };
  }, []);


  const calculateObjectiveProgress = (tasks = []) => {
    if (!tasks || tasks.length === 0) return 0;
    const total = tasks.reduce((s, t) => s + (t.progressPercent || 0), 0);
    return Math.round(total / tasks.length);
  };

  const extractAttributes = (attributes = []) => {
    const map = {};
    attributes.forEach((attr) => (map[attr.attribute] = attr.value));
    return map;
  };

  const getIncumbentId = (position) => {
    if (!position || !position.attributes) return null;
    const attrs = extractAttributes(position.attributes);
    return attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] || null;
  };

  const debounceTimeouts = useRef({});

  const debounceSave = useCallback((key, fn, delay = 500) => {
    if (debounceTimeouts.current[key]) {
      clearTimeout(debounceTimeouts.current[key]);
    }
    debounceTimeouts.current[key] = setTimeout(() => {
      fn();
      delete debounceTimeouts.current[key];
    }, delay);
  }, []);

  useEffect(() => {
    if (!positionTeiString || !user?.id) {
      setIsEmployee(false);
      setIsManager(false);
      setRoleCheckComplete(true);
      return;
    }

    let cancelled = false;

    const resolveRoles = async () => {
      try {
        const positionRes = await engine.query({
          position: {
            resource: `trackedEntityInstances/${positionTeiString}`,
            params: { fields: 'attributes[attribute,value]' }
          }
        });

        const posAttrs = extractAttributes(positionRes.position?.attributes || []);
        const incumbentIdentifier = posAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];

        if (!incumbentIdentifier) {
          console.warn('No incumbent identifier found for position');
          if (!cancelled) {
            setIsEmployee(false);
            setIsManager(false);
            setRoleCheckComplete(true);
          }
          return;
        }

        const isEmp = user?.id === incumbentIdentifier || user?.username === incumbentIdentifier;

        let isMgr = false;
        try {
          const posAttrs = extractAttributes(positionRes.position?.attributes || []);
          const managerPositionTei = posAttrs[POSITION_ATTRIBUTES.REPORTING_TO];

          if (managerPositionTei) {
            const mgrPosRes = await engine.query({
              mgrPosition: {
                resource: `trackedEntityInstances/${managerPositionTei}`,
                params: { fields: 'attributes[attribute,value]' }
              }
            });
            const mgrAttrs = extractAttributes(mgrPosRes.mgrPosition?.attributes || []);
            const mgrUserIdentifier = mgrAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
            isMgr = user?.id === mgrUserIdentifier || user?.username === mgrUserIdentifier;
          }
        } catch (err) {
          console.warn('Failed to check manager role:', err);
        }

        if (!cancelled) {
          setIsEmployee(isEmp);
          setIsManager(isMgr);
          setRoleCheckComplete(true);
          console.log('🔐 Roles resolved:', {
            userId: user?.id,
            incumbentIdentifier,
            isEmployee: isEmp,
            isManager: isMgr
          });
        }

      } catch (err) {
        console.error('Role resolution failed:', err);
        if (!cancelled) {
          setIsEmployee(user?.id === positionTeiString);
          setIsManager(false);
          setRoleCheckComplete(true);
        }
      }
    };

    resolveRoles();
    return () => { cancelled = true; };
  }, [positionTeiString, user?.id, user?.username, engine]);


  const isFinalizedAndLocked = appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED;
  const canEmployeeEdit = !readOnlyReport &&
    isEmployee && !isFinalizedAndLocked &&
    appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.DRAFT;
  const canManagerEdit = !readOnlyReport &&
    appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED &&
    (isManager || managerCanView);

  const getSixMonthsAgo = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  };

  
  const recentObjectivesRef = useRef([]);

  useEffect(() => {
    recentObjectivesRef.current = recentObjectives;
  }, [recentObjectives]);

  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      debounceTimeouts.current = {};
    };
  }, []);

useEffect(() => {
  if (!positionTeiString || !programStage || !orgUnit) return;
  let cancelled = false;

  const loadAppraisal = async () => {
    try {
      // Use the passed appraisalEvent if available (from parent)
      if (appraisalEvent) {
        console.log('📋 Loading appraisal from parent event:', {
          eventId: appraisalEvent.eventId,
          rawStatus: appraisalEvent.appraisalStatus,
          employeeTask: appraisalEvent.employeeSelfRatingTask,
          employeeBehavioural: appraisalEvent.employeeSelfRatingBehavioural,
          employeeComments: appraisalEvent.employeeSelfComments,
        });

        if (!cancelled) {
          setAppraisalData({
            employeeSelfRatingTask: appraisalEvent.employeeSelfRatingTask || '',
            employeeSelfRatingBehavioural: appraisalEvent.employeeSelfRatingBehavioural || '',
            employeeSelfComments: appraisalEvent.employeeSelfComments || '',
            managerRatingTask: appraisalEvent.managerRatingTask || '',
            managerRatingBehavioural: appraisalEvent.managerRatingBehavioural || '',
            managerComments: appraisalEvent.managerComments || '',
            managerBusinessComments: appraisalEvent.managerBusinessComments || '',
            appraisalStatus: appraisalEvent.appraisalStatus || APPRAISAL_STATUS_CODES.DRAFT,
            eventId: appraisalEvent.eventId,

            employeeCompetencyRatings: appraisalEvent.employeeCompetencyRatings || '',
            employeeCompetencyComments: appraisalEvent.employeeCompetencyComments || '',
            managerCompetencyRatings: appraisalEvent.managerCompetencyRatings || '',
            managerCompetencyComments: appraisalEvent.managerCompetencyComments || '',
          });

          // ✅ Load employee response data from parent event
          if (appraisalEvent.employeeResponseStatus) {
            setEmployeeResponseStatus(appraisalEvent.employeeResponseStatus);
            setEmployeeResponseRemarks(appraisalEvent.employeeResponseRemarks || '');
            setEmployeeAcceptedRatingTask(appraisalEvent.employeeAcceptedRatingTask || '');
            setEmployeeAcceptedRatingBehavioural(appraisalEvent.employeeAcceptedRatingBehavioural || '');
          }
        }
        return;
      }

      // Otherwise fetch from API
      const response = await engine.query({
        appraisal: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage,
            orgUnit,
            ouMode: 'ACCESSIBLE',
            trackedEntityInstance: positionTeiString,
            fields: 'event,dataValues[dataElement,value],status',
            pageSize: 1,
          },
        },
      });

      const event = response.appraisal?.events?.[0];
      if (event) {
        const dv = (de) => event.dataValues?.find(d => d.dataElement === de)?.value || '';
        const rawStatus = dv(DATA_ELEMENTS.APPRAISAL_STATUS);

        console.log('📋 Loading appraisal from API:', {
          eventId: event.event,
          rawStatus,
          employeeTask: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK),
          employeeBehavioural: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL),
        });

        if (!cancelled) {
          setAppraisalData({
            employeeSelfRatingTask: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK) || '',
            employeeSelfRatingBehavioural: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL) || '',
            employeeSelfComments: dv(DATA_ELEMENTS.EMPLOYEE_COMMENTS) || '',
            managerRatingTask: dv(DATA_ELEMENTS.MANAGER_RATING_TASK) || '',
            managerRatingBehavioural: dv(DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL) || '',
            managerComments: dv(DATA_ELEMENTS.MANAGER_COMMENTS) || '',
            managerBusinessComments: dv(DATA_ELEMENTS.MANAGER_BUSINESS_COMMENTS) || '',
            appraisalStatus: rawStatus ? rawStatus.toLowerCase() : APPRAISAL_STATUS_CODES.DRAFT,
            eventId: event.event,

            employeeCompetencyRatings: dv(DATA_ELEMENTS.EMPLOYEE_COMPETENCY_RATINGS) || '',
            employeeCompetencyComments: dv(DATA_ELEMENTS.EMPLOYEE_COMPETENCY_COMMENTS) || '',
            managerCompetencyRatings: dv(DATA_ELEMENTS.MANAGER_COMPETENCY_RATINGS) || '',
            managerCompetencyComments: dv(DATA_ELEMENTS.MANAGER_COMPETENCY_COMMENTS) || '',
          });

          // ✅ Load employee response data from API
          const responseStatus = dv(DATA_ELEMENTS.EMPLOYEE_RESPONSE_STATUS);
          const responseRemarks = dv(DATA_ELEMENTS.EMPLOYEE_RESPONSE_REMARKS);
          const acceptedTask = dv(DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_TASK);
          const acceptedBehavioural = dv(DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_BEHAVIOURAL);
          
          if (responseStatus) {
            setEmployeeResponseStatus(responseStatus);
            setEmployeeResponseRemarks(responseRemarks || '');
            setEmployeeAcceptedRatingTask(acceptedTask || '');
            setEmployeeAcceptedRatingBehavioural(acceptedBehavioural || '');
          }
        }

        console.log('📦 managerRatingTask from API:', dv(DATA_ELEMENTS.MANAGER_RATING_TASK));

      } else {
        console.log('ℹ️ No existing appraisal found, starting fresh');
        // Reset to fresh state
        if (!cancelled) {
          setAppraisalData({
            employeeSelfRatingTask: '',
            employeeSelfRatingBehavioural: '',
            employeeSelfComments: '',
            managerRatingTask: '',
            managerRatingBehavioural: '',
            managerComments: '',
            managerBusinessComments: '',
            appraisalStatus: APPRAISAL_STATUS_CODES.DRAFT,
            eventId: null,
          });
        }
      }
    } catch (err) {
      console.error('Failed to load appraisal:', err);
    }
  };

  loadAppraisal();
  return () => { cancelled = true; };
}, [positionTeiString, programStage, orgUnit, engine, appraisalEvent]);

useEffect(() => {
  if (!positionTeiString || !orgUnit) {
    console.warn('⏳ No position or orgUnit – skipping objectives load');
    return;
  }

  const start = effectiveStart;
  const end = effectiveEnd;

  if (!start || !end) {
    console.error('❌ Fiscal year dates (effectiveStart/end) are missing:', { start, end });
    setRecentObjectives([]);
    return;
  }

  console.log('🔍 Loading objectives for fiscal year:', { start, end });

  let cancelled = false;

  const loadRecentObjectives = async () => {
    try {
      console.log('📤 Sending objectives query...');

      const objectivesResponse = await engine.query({
        objectives: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage: 'VIBtpvkpHFR',
            orgUnit: orgUnit,
            ouMode: 'ACCESSIBLE',
            fields: 'event,trackedEntityInstance,orgUnit,eventDate,dataValues[dataElement,value]',
            eventStartDate: start,
            eventEndDate: end,
            pageSize: 100,
          },
        },
      });

      if (cancelled) return;

      console.log('✅ Objectives query completed. Raw response:', objectivesResponse);

      const events = objectivesResponse.objectives?.events || [];
      console.log(`📊 Found ${events.length} raw objectives`);

      const allObjs = events.map((event) => ({
        id: event.event,
        trackedEntityInstance: event.trackedEntityInstance,
        name: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.NAME)?.value || 'Unnamed Objective',
        description: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.DESCRIPTION)?.value || '',
        startDate: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.START_DATE)?.value || '',
        completionDate: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.COMPLETION_DATE)?.value || '',
        approvalStatus: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.APPROVAL_STATUS)?.value || 'pending',
        positionTei: event.dataValues?.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value,
        eventDate: event.eventDate,
        dataValues: event.dataValues || [],
      }));

      console.log(`📦 ${allObjs.length} objectives before manual filter`);

      // ---- MANUAL FISCAL YEAR FILTER (string comparison) ----
      const startStr = start;
      const endStr = end;
      const filteredObjs = allObjs.filter(obj => {
        if (!obj.eventDate) return false;
        const dateStr = obj.eventDate.split('T')[0];
        return dateStr >= startStr && dateStr <= endStr;
      });

      console.log(`📊 ${allObjs.length} objectives before filter, ${filteredObjs.length} after`);

      if (filteredObjs.length === 0) {
        console.warn('⚠️ No objectives remain after fiscal year filter.');
        setRecentObjectives([]);
        return;
      }

      // --- Fetch KPIs ---
      const kpisResponse = await engine.query({
        allKpis: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage: 'ivyCLjNLFrd',
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 200,
          },
        },
      });

      const allKpis = kpisResponse.allKpis?.events || [];
      console.log(`📊 Found ${allKpis.length} total KPIs`);

      // --- Fetch tasks (for progress) ---
      const tasksResponse = await engine.query({
        allTasks: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage: 'Nv9nYNcBTdM',
            filter: [`SlgO60cMK3F:eq:${positionTeiString}`],
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 1000,
          },
        },
      });

      const allTasks = tasksResponse.allTasks?.events || [];
      console.log(`📋 Found ${allTasks.length} tasks for position ${positionTeiString}`);

      // Build task progress map by KPI
      const taskProgressByKpi = {};
      allTasks.forEach(event => {
        const dv = event.dataValues || [];
        const kpiId = dv.find(d => d.dataElement === 'uXeH0X0aaCl')?.value;
        const progress = parseInt(dv.find(d => d.dataElement === 'AElpNxzTdRh')?.value) || 0;
        if (kpiId) {
          if (!taskProgressByKpi[kpiId]) {
            taskProgressByKpi[kpiId] = { totalProgress: 0, count: 0 };
          }
          taskProgressByKpi[kpiId].totalProgress += progress;
          taskProgressByKpi[kpiId].count += 1;
        }
      });

      // Group KPIs by objective, using filteredObjs
      const kpisByObjective = {};

      allKpis.forEach(event => {
        const dataValues = event.dataValues || [];
        const objectiveId = dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
        const kpiPositionTei = dataValues.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;

        // Check if this KPI belongs to any objective in filteredObjs
        const objective = filteredObjs.find(obj => obj.id === objectiveId);
        const isRelevant =
          kpiPositionTei === positionTeiString ||
          (objective && objective.positionTei === positionTeiString);

        if (objectiveId && isRelevant) {
          if (!kpisByObjective[objectiveId]) {
            kpisByObjective[objectiveId] = [];
          }

          const kpiName = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 'Unnamed KPI';
          const kpiTarget = dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value || '';
          const kpiAchieved = dataValues.find(dv => dv.dataElement === 'pAGDYpY5X6f')?.value || '0';

          kpisByObjective[objectiveId].push({
            id: event.event,
            name: kpiName,
            target: kpiTarget,
            achieved: parseFloat(kpiAchieved) || 0,
            progress: Math.min(100, Math.round((parseFloat(kpiAchieved) / parseFloat(kpiTarget || 1)) * 100) || 0),
            status: dataValues.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value || '',
            completionDate: dataValues.find(dv => dv.dataElement === 'J0rjxrDKn1l')?.value || '',
          });

          // Override progress with actual task progress
          const taskData = taskProgressByKpi[event.event];
          if (taskData && taskData.count > 0) {
            const avgTaskProgress = Math.round(taskData.totalProgress / taskData.count);
            kpisByObjective[objectiveId][kpisByObjective[objectiveId].length - 1].progress = avgTaskProgress;
            kpisByObjective[objectiveId][kpisByObjective[objectiveId].length - 1].achieved = avgTaskProgress;
          }
        }
      });

      // Filter objectives linked to this position (direct match or via data element)
      const objectivesForPosition = filteredObjs.filter(obj => {
        const directMatch = obj.trackedEntityInstance === positionTeiString;
        const dataElementMatch = obj.positionTei === positionTeiString;
        return directMatch || dataElementMatch;
      });

      console.log(`🎯 Objectives for position ${positionTeiString}: ${objectivesForPosition.length}`);
      objectivesForPosition.forEach(obj => {
        console.log(`   - "${obj.name}" (${obj.eventDate})`);
      });

      // Calculate KPI progress and count per objective
      const objectivesWithProgress = objectivesForPosition.map(obj => {
        const objKpis = kpisByObjective[obj.id] || [];
        const kpiProgress = objKpis.length > 0
          ? objKpis.reduce((sum, k) => sum + k.progress, 0) / objKpis.length
          : 0;
        const completedKpis = objKpis.filter(k => k.progress >= 100).length;

        return {
          ...obj,
          kpis: objKpis,
          progress: Math.round(kpiProgress),
          kpiCount: objKpis.length,
          completedKpis: completedKpis,
        };
      });

      objectivesWithProgress.sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));

      if (!cancelled) {
        setRecentObjectives(objectivesWithProgress);
        console.log(`✅ Set ${objectivesWithProgress.length} objectives with KPI data`);
        objectivesWithProgress.forEach(obj => {
          console.log(`   - "${obj.name}" → KPIs: ${obj.kpiCount}, Progress: ${obj.progress}%`);
        });
      }
    } catch (err) {
      console.error('❌ Error in loadRecentObjectives:', err);
      setRecentObjectives([]);
    }
  };

  loadRecentObjectives();
  return () => { cancelled = true; };
}, [positionTeiString, orgUnit, effectiveStart, effectiveEnd, engine]);

  const saveAppraisal = async (status) => {
     try {
        setLoading(true);

        // Convert competency ratings to JSON string for storage
        const competencyRatingsJSON = JSON.stringify(competencyRatings);
        const competencyCommentsJSON = JSON.stringify(competencyComments);
        const managerCompetencyRatingsJSON = JSON.stringify(managerCompetencyRatings);
        const managerCompetencyCommentsJSON = JSON.stringify(managerCompetencyComments);

        const dataValues = [
            { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
            { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
            { dataElement: DATA_ELEMENTS.EMPLOYEE_COMMENTS, value: appraisalData.employeeSelfComments || '' },
            { dataElement: DATA_ELEMENTS.MANAGER_RATING_TASK, value: appraisalData.managerRatingTask || '' },
            { dataElement: DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL, value: appraisalData.managerRatingBehavioural || '' },
            { dataElement: DATA_ELEMENTS.MANAGER_COMMENTS, value: appraisalData.managerComments || '' },
            { dataElement: DATA_ELEMENTS.MANAGER_BUSINESS_COMMENTS, value: appraisalData.managerBusinessComments || '' },
            { dataElement: DATA_ELEMENTS.APPRAISAL_STATUS, value: status },
            // ✅ Save competency ratings
            { dataElement: DATA_ELEMENTS.EMPLOYEE_COMPETENCY_RATINGS, value: competencyRatingsJSON || '' },
            { dataElement: DATA_ELEMENTS.EMPLOYEE_COMPETENCY_COMMENTS, value: competencyCommentsJSON || '' },
            { dataElement: DATA_ELEMENTS.MANAGER_COMPETENCY_RATINGS, value: managerCompetencyRatingsJSON || '' },
            { dataElement: DATA_ELEMENTS.MANAGER_COMPETENCY_COMMENTS, value: managerCompetencyCommentsJSON || '' },
      ];

      const eventData = {
        program: 'yiT00XKrrHP',
        programStage,
        orgUnit,
        trackedEntityInstance: positionTeiString,
        eventDate: new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
        dataValues,
      };

      if (!appraisalData.eventId) {
        const checkResponse = await engine.query({
          check: {
            resource: 'events',
            params: {
              program: 'yiT00XKrrHP',
              programStage,
              orgUnit,
              trackedEntityInstance: positionTeiString,
              fields: 'event',
              pageSize: 1
            }
          }
        });

        const existingEvent = checkResponse.check?.events?.[0]?.event;
        if (existingEvent) {
          appraisalData.eventId = existingEvent;
        }
      }

      if (appraisalData.eventId) {
        await engine.mutate({
          resource: `events/${appraisalData.eventId}`,
          type: 'update',
          data: eventData,
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        const result = await engine.mutate({
          resource: 'events',
          type: 'create',
          data: eventData,
          headers: { 'Content-Type': 'application/json' },
        });
        setAppraisalData(prev => ({ ...prev, eventId: result.response?.reference || prev.eventId }));
      }

      setAppraisalData(prev => ({ ...prev, appraisalStatus: status }));
      alert(`Appraisal ${status === APPRAISAL_STATUS_CODES.DRAFT ? 'saved as draft' : 'submitted'} successfully!`);
    } catch (err) {
      console.error('Save error:', err);
      let errorMsg = 'Failed to save appraisal';
      if (err?.response?.body?.message) {
        errorMsg = err.response.body.message;
      }
      setError(`Save failed: ${errorMsg}`);
      alert(`❌ ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // NEW FUNCTIONS FOR TWO-WAY APPROVAL FLOW
  // ============================================================
    const getAuthHeader = () => {
      // Get the basic auth credentials from localStorage or context
      const basicAuth = localStorage.getItem('basicAuth');
      if (basicAuth) {
        return { 'Authorization': `Basic ${basicAuth}` };
      }
      // Fallback to getting from session
      const auth = sessionStorage.getItem('auth');
      if (auth) {
        try {
          const { username, password } = JSON.parse(auth);
          const encoded = btoa(`${username}:${password}`);
          return { 'Authorization': `Basic ${encoded}` };
        } catch (e) {
          console.warn('Failed to parse auth');
        }
      }
      return {};
    };

    // Helper function to save appraisal data using direct fetch
  const saveAppraisalData = async (dataValues, status) => {
    if (appraisalData.eventId) {
      const fullEventResponse = await engine.query({
        event: {
          resource: `events/${appraisalData.eventId}`,
          params: { fields: 'event,program,programStage,orgUnit,trackedEntityInstance,eventDate,status,enrollment,dataValues[dataElement,value]' }
        }
      });

      const fullEvent = fullEventResponse.event;
      if (!fullEvent) throw new Error('Event not found');

      const existingDataValues = fullEvent.dataValues || [];
      const updatedDataValues = [...existingDataValues];

      dataValues.forEach(newDv => {
        const idx = updatedDataValues.findIndex(dv => dv.dataElement === newDv.dataElement);
        if (idx >= 0) {
          updatedDataValues[idx] = { dataElement: newDv.dataElement, value: newDv.value };
        } else {
          updatedDataValues.push({ dataElement: newDv.dataElement, value: newDv.value });
        }
      });

      await engine.mutate({
        type: 'update',
        resource: `events/${appraisalData.eventId}`,
        data: {
          ...fullEvent,
          dataValues: updatedDataValues,
        },
        headers: { 'Content-Type': 'application/json' }
      });
    } else {
      const result = await engine.mutate({
        resource: 'events',
        type: 'create',
        data: {
          program: 'yiT00XKrrHP',
          programStage,
          orgUnit,
          trackedEntityInstance: positionTeiString,
          eventDate: new Date().toISOString().split('T')[0],
          status: 'ACTIVE',
          dataValues,
        },
        headers: { 'Content-Type': 'application/json' },
      });
      setAppraisalData(prev => ({ ...prev, eventId: result.response?.reference || prev.eventId }));
    }

    setAppraisalData(prev => ({ ...prev, appraisalStatus: status }));
  };

  // Handle employee finalizing the appraisal after manager review
const handleFinalizeAfterReview = async (remarks, agrees) => {
  if (!remarks || !remarks.trim()) {
    alert('Please provide remarks before finalizing.');
    return;
  }

  try {
    setLoading(true);
    
    // Set the response data in state
    setEmployeeResponseStatus(agrees ? 'accepted' : 'disputed');
    setEmployeeResponseRemarks(remarks);
    setEmployeeAcceptedRatingTask(appraisalData.managerRatingTask || '');
    setEmployeeAcceptedRatingBehavioural(appraisalData.managerRatingBehavioural || '');

    const dataValues = [
      { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_COMMENTS, value: appraisalData.employeeSelfComments || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_RATING_TASK, value: appraisalData.managerRatingTask || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL, value: appraisalData.managerRatingBehavioural || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_COMMENTS, value: appraisalData.managerComments || '' },
      { dataElement: DATA_ELEMENTS.MANAGER_BUSINESS_COMMENTS, value: appraisalData.managerBusinessComments || '' },
      { dataElement: DATA_ELEMENTS.APPRAISAL_STATUS, value: APPRAISAL_STATUS_CODES.FINALIZED },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_RESPONSE_STATUS, value: agrees ? 'accepted' : 'disputed' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_RESPONSE_REMARKS, value: remarks },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_TASK, value: appraisalData.managerRatingTask || '' },
      { dataElement: DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_BEHAVIOURAL, value: appraisalData.managerRatingBehavioural || '' },
    ];

    await saveAppraisalData(dataValues, APPRAISAL_STATUS_CODES.FINALIZED);
    alert('✓ Appraisal finalized successfully!');
  } catch (err) {
    console.error('Failed to finalize appraisal:', err);
    alert('Failed to finalize appraisal. Please try again.');
  } finally {
    setLoading(false);
  }
};

  
    // Handle manager updating to REVIEWED status (after providing rating)
  const handleManagerReview = async () => {
    if (!appraisalData.managerRatingTask || !appraisalData.managerRatingBehavioural) {
      alert('Please provide both Task and Behavioural ratings before reviewing.');
      return;
    }

    // Validate eventId exists
    if (!appraisalData.eventId) {
      alert('No appraisal event found. Please save draft first.');
      return;
    }

    try {
      setLoading(true);

      const dataValues = [
        { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_COMMENTS, value: appraisalData.employeeSelfComments || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_RATING_TASK, value: appraisalData.managerRatingTask || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL, value: appraisalData.managerRatingBehavioural || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_COMMENTS, value: appraisalData.managerComments || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_BUSINESS_COMMENTS, value: appraisalData.managerBusinessComments || '' },
        { dataElement: DATA_ELEMENTS.APPRAISAL_STATUS, value: APPRAISAL_STATUS_CODES.MANAGER_REVIEWED },
      ];

      await saveAppraisalData(dataValues, APPRAISAL_STATUS_CODES.MANAGER_REVIEWED);
      
      // Update local state
      setAppraisalData(prev => ({ 
        ...prev, 
        appraisalStatus: APPRAISAL_STATUS_CODES.MANAGER_REVIEWED 
      }));
      
      alert('✓ Manager review submitted. Employee will now respond.');
    } catch (err) {
      console.error('Failed to submit manager review:', err);
      alert(`Failed to submit review: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle submit with auto-approval for top-level users
  const handleSubmit = async () => {
    try {
      setLoading(true);

      // For top-level users, auto-approve and finalize
      if (isTopLevelUser) {
        const dataValues = [
          { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
          { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
          { dataElement: DATA_ELEMENTS.EMPLOYEE_COMMENTS, value: appraisalData.employeeSelfComments || '' },
          { dataElement: DATA_ELEMENTS.MANAGER_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
          { dataElement: DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
          { dataElement: DATA_ELEMENTS.MANAGER_COMMENTS, value: 'Auto-approved - CEO/Top-Level Executive' },
          { dataElement: DATA_ELEMENTS.APPRAISAL_STATUS, value: APPRAISAL_STATUS_CODES.FINALIZED },
          { dataElement: DATA_ELEMENTS.EMPLOYEE_RESPONSE_STATUS, value: 'accepted' },
          { dataElement: DATA_ELEMENTS.EMPLOYEE_RESPONSE_REMARKS, value: 'Auto-approved (CEO/Top-Level)' },
        ];

        const eventData = {
          program: 'yiT00XKrrHP',
          programStage,
          orgUnit,
          trackedEntityInstance: positionTeiString,
          eventDate: new Date().toISOString().split('T')[0],
          status: 'ACTIVE',
          dataValues,
        };

        if (!appraisalData.eventId) {
          const checkResponse = await engine.query({
            check: {
              resource: 'events',
              params: {
                program: 'yiT00XKrrHP',
                programStage,
                orgUnit,
                trackedEntityInstance: positionTeiString,
                fields: 'event',
                pageSize: 1
              }
            }
          });

          const existingEvent = checkResponse.check?.events?.[0]?.event;
          if (existingEvent) {
            appraisalData.eventId = existingEvent;
          }
        }

        if (appraisalData.eventId) {
          await engine.mutate({
            resource: `events/${appraisalData.eventId}`,
            type: 'update',
            data: eventData,
            headers: { 'Content-Type': 'application/json' },
          });
        } else {
          const result = await engine.mutate({
            resource: 'events',
            type: 'create',
            data: eventData,
            headers: { 'Content-Type': 'application/json' },
          });
          setAppraisalData(prev => ({ ...prev, eventId: result.response?.reference || prev.eventId }));
        }

        setAppraisalData(prev => ({ ...prev, appraisalStatus: APPRAISAL_STATUS_CODES.FINALIZED }));
        alert('✓ Appraisal submitted and auto-approved!');
      } else {
        // Normal user - just submit for review
        await saveAppraisal(APPRAISAL_STATUS_CODES.SUBMITTED);
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('Failed to submit appraisal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const computed = useMemo(() => ({
    employeeTotal: ((safeParse(appraisalData.employeeSelfRatingTask) + safeParse(appraisalData.employeeSelfRatingBehavioural)) / 2).toFixed(2),
    managerTotal: ((safeParse(appraisalData.managerRatingTask) + safeParse(appraisalData.managerRatingBehavioural)) / 2).toFixed(2),
  }), [appraisalData]);

  if (loading) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Saving appraisal…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '1rem' }}>
        <NoticeBox error title="Error">{error}</NoticeBox>
      </div>
    );
  }

  const isMidYear = programStage === 'xbEyVHbXlGK';

  if (!roleCheckComplete) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Checking permissions…</span>
      </div>
    );
  }

  return (
    <div className="apr-page">
      {/* ── Workflow Stepper ─────────────────────────────────── */}
      <WorkflowStepper
        currentStatus={appraisalData.appraisalStatus}
        isTopLevelUser={isTopLevelUser}
      />

      {/* Status Information Banner - Simple version */}
      <div style={{
        marginBottom: '1.25rem',
        padding: '0.6rem 1rem',
        backgroundColor: 'var(--apr-neutral-light)',
        borderRadius: '8px',
        border: '1px solid var(--apr-border-light)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Clock size={16} color="var(--apr-text-secondary)" />
          <span style={{ fontSize: '0.85rem', color: 'var(--apr-text)', fontWeight: 600 }}>
            {appraisalData.appraisalStatus?.toUpperCase() || 'DRAFT'}
          </span>
        </div>
        <span style={{ height: '14px', width: '1px', backgroundColor: 'var(--apr-border)' }} />
        <span style={{ fontSize: '0.8rem', color: 'var(--apr-text-secondary)', fontWeight: 500 }}>
          {STATUS_HINTS[appraisalData.appraisalStatus] || ' Complete the required sections below.'}
        </span>
      </div>

      {/* ── Recent Objectives ────────────────────────────────── */}
      <div className="apr-section">
        <div className="apr-card">
          <div className="apr-card-header">
            <div className="apr-card-header-left">
              <span className="apr-card-icon"><TrendingUp size={17} /></span>
              <h4 className="apr-card-title">Objective Progress — Last 6 Months</h4>
              {recentObjectives.length > 0 && (
                <span className="apr-count-badge">{recentObjectives.length}</span>
              )}
            </div>
          </div>

          <div className="apr-card-body">
            {recentObjectives.length > 0 ? (
              <>
                 {/* Column Headers */}
                <div className="apr-obj-header">
                  <div style={{ flex: 1 }}> Business Objective</div>
                  <div style={{ width: 160 }}>KPI Progress</div>
                  <div style={{ width: 110, textAlign: 'center' }}>KPIs</div>
                  <div style={{ width: 105, textAlign: 'center' }}>Status</div>
                  <div style={{ width: 105, textAlign: 'center' }}>KPIs Done</div>
                </div>

                {/* Objective Rows */}
                {recentObjectives.map(obj => {
                  const handleRatingChange = (value) => {
                    if (value === '') {
                      setRecentObjectives(prev =>
                        prev.map(o =>
                          o.id === obj.id
                            ? {
                              ...o,
                              dataValues: [
                                ...(o.dataValues?.filter(dv => dv.dataElement !== OBJECTIVE_APPRAISAL_DE.SELF_RATING) || []),
                                { dataElement: OBJECTIVE_APPRAISAL_DE.SELF_RATING, value: '' }
                              ]
                            }
                            : o
                        )
                      );
                      return;
                    }

                    let numValue = parseFloat(value);
                    if (isNaN(numValue)) return;
                    numValue = Math.max(0, Math.min(5, numValue));
                    const clampedValue = parseFloat(numValue.toFixed(1));

                    setRecentObjectives(prev =>
                      prev.map(o =>
                        o.id === obj.id
                          ? {
                            ...o,
                            dataValues: [
                              ...(o.dataValues?.filter(dv => dv.dataElement !== OBJECTIVE_APPRAISAL_DE.SELF_RATING) || []),
                              { dataElement: OBJECTIVE_APPRAISAL_DE.SELF_RATING, value: String(clampedValue) }
                            ]
                          }
                          : o
                      )
                    );

                    debounceSave(
                      `${obj.id}-rating`,
                      () => saveObjectiveRating(obj.id, OBJECTIVE_APPRAISAL_DE.SELF_RATING, String(clampedValue))
                    );
                  };

                  const handleCommentsChange = (value) => {
                    setRecentObjectives(prev =>
                      prev.map(o =>
                        o.id === obj.id
                          ? {
                            ...o,
                            dataValues: [
                              ...(o.dataValues?.filter(dv => dv.dataElement !== OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS) || []),
                              { dataElement: OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS, value }
                            ]
                          }
                          : o
                      )
                    );
                    debounceSave(
                      `${obj.id}-comments`,
                      () => saveObjectiveRating(obj.id, OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS, value)
                    );
                  };

                  return (
                    <ObjectiveRow
                      key={obj.id}
                      obj={obj}
                      isExpanded={expandedObjectiveId === obj.id}
                      onToggle={() => setExpandedObjectiveId(
                        expandedObjectiveId === obj.id ? null : obj.id
                      )}
                      savingObjectiveId={savingObjectiveId}
                      onRatingChange={handleRatingChange}
                      onCommentsChange={handleCommentsChange}
                      disabled={!canEmployeeEdit}
                    />
                  );
                })}
              </>
            ) : (
              <div className="apr-no-objectives">
                <div className="apr-no-objectives-icon"><TrendingUp size={36} /></div>
                <div className="apr-no-objectives-text">No objectives found for the last 6 months.</div>
              </div>
            )}
          </div>
        </div>
      </div>

     
      {/* Employee Self-Appraisal Section with enhanced UI */}
      {(isEmployee || (isManager && appraisalData.appraisalStatus !== APPRAISAL_STATUS_CODES.DRAFT)) && (
        <div className="apr-section">
          <div className="apr-card">
            <div className="apr-card-header">
              <div className="apr-card-header-left">
                <span className="apr-card-icon"><Star size={20} /></span>
                <h4 className="apr-card-title">
                  {isEmployee ? "Overall Self-Evaluation" : "Employee's Self-Evaluation Review"}
                </h4>
              </div>
              <StatusChip status={appraisalData.appraisalStatus} />
            </div>

            <div className="apr-subsection apr-subsection--business">
          <div className="apr-subsection-header">
            <h5>📊 Business Performance Objectives</h5>
            <span className="apr-subsection-weight">{validBusinessWeight}% Weight</span>
          </div>
          
           <CriteriaSection 
              description={`Achievement evaluation against set SMART objectives`}
            >
              <div>
                <div className="apr-score-header-row">
                  <div className="apr-score-cell-main">Evaluation Criteria</div>
                  <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>Rating</div>
                  <div className="apr-score-cell-weight">Weight</div>
                  <div className="apr-score-cell-result">Weighted Score</div>
                </div>

                {/* ---- Employee's Self Rating ---- */}
                {canEmployeeEdit ? (
                  <BusinessScoreRow
                    label="Objective Achievement Level"
                    hint={`Reflect on your progress across all business objectives (${validBusinessWeight}% of total)`}
                    value={appraisalData.employeeSelfRatingTask}
                    score={((safeParse(appraisalData.employeeSelfRatingTask) * validBusinessWeight) / 100).toFixed(2)}
                    onChange={(value) => {
                      if (value === '') { setAppraisalData(prev => ({ ...prev, employeeSelfRatingTask: '' })); return; }
                      setAppraisalData(prev => ({ ...prev, employeeSelfRatingTask: value }));
                    }}
                    weight={`${validBusinessWeight}%`}
                    disabled={!canEmployeeEdit}
                    showRatingHelp={true}
                    scaleType="business"
                  />
                ) : (
                  <div className="apr-score-row">
                    <div className="apr-score-cell-main">
                      <div className="apr-score-label" style={{ color: '#0d47a1' }}>
                        {isManager && !isEmployee ? '👤 Employee\'s Self Rating' : 'Objective Achievement Level'}
                      </div>
                      <div className="apr-score-hint">
                        {isManager && !isEmployee 
                          ? 'Employee\'s evaluation of their business objective achievement' 
                          : 'Evaluation of results against business targets'}
                      </div>
                    </div>
                    <div className="apr-score-cell-rating">
                      <div style={{
                        padding: '10px',
                        backgroundColor: appraisalData.employeeSelfRatingTask ? businessRatingScale[appraisalData.employeeSelfRatingTask]?.bgColor || '#f1f5f9' : '#f8f9fa',
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: `1px solid ${appraisalData.employeeSelfRatingTask ? businessRatingScale[appraisalData.employeeSelfRatingTask]?.color || '#cbd5e1' : '#e2e8f0'}`,
                        fontWeight: 700,
                        color: appraisalData.employeeSelfRatingTask ? businessRatingScale[appraisalData.employeeSelfRatingTask]?.color : '#64748b'
                      }}>
                        {appraisalData.employeeSelfRatingTask ? `${appraisalData.employeeSelfRatingTask} - ${businessRatingScale[appraisalData.employeeSelfRatingTask]?.label}` : '—'}
                      </div>
                    </div>
                    <div className="apr-score-cell-weight" style={{ textAlign: 'center' }}>{validBusinessWeight}%</div>
                    <div className="apr-score-cell-result" style={{ textAlign: 'center', fontWeight: 800 }}>
                      {((safeParse(appraisalData.employeeSelfRatingTask) * validBusinessWeight) / 100).toFixed(2)}
                    </div>
                  </div>
                )}

                {/* ---- Manager's Rating (shown when manager is viewing a subordinate) ---- */}
                {isManager && !isEmployee && (
                  <div className="apr-score-row" style={{ 
                    borderTop: '2px dashed #e2e8f0', 
                    marginTop: '4px',
                    paddingTop: '12px'
                  }}>
                    <div className="apr-score-cell-main">
                      <div className="apr-score-label" style={{ color: '#4f46e5' }}>👔 Manager's Recommended Rating</div>
                      <div className="apr-score-hint">Your evaluation of this employee's business objective achievement</div>
                    </div>
                    <div className="apr-score-cell-rating">
                      {console.log('🔍 managerRatingTask value:', appraisalData.managerRatingTask)}
                      {canManagerEdit ? (
                        <InputField
                          type="number"
                          value={String(appraisalData.managerRatingTask ?? '')}
                          onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerRatingTask: value }))}
                          min="0"
                          max="5"
                          step="1"
                          inputWidth="100%"
                          placeholder="0-5"
                        />
                      ) : (
                        <div style={{
                          padding: '10px',
                          backgroundColor: appraisalData.managerRatingTask ? businessRatingScale[appraisalData.managerRatingTask]?.bgColor || '#f1f5f9' : '#f8f9fa',
                          borderRadius: '8px',
                          textAlign: 'center',
                          border: `1px solid ${appraisalData.managerRatingTask ? businessRatingScale[appraisalData.managerRatingTask]?.color || '#cbd5e1' : '#e2e8f0'}`,
                          fontWeight: 700,
                          color: appraisalData.managerRatingTask ? businessRatingScale[appraisalData.managerRatingTask]?.color : '#64748b'
                        }}>
                          {appraisalData.managerRatingTask ? `${appraisalData.managerRatingTask} - ${businessRatingScale[appraisalData.managerRatingTask]?.label}` : 'Not rated'}
                        </div>
                      )}
                    </div>
                    <div className="apr-score-cell-weight" style={{ textAlign: 'center' }}>{validBusinessWeight}%</div>
                    <div className="apr-score-cell-result" style={{ textAlign: 'center', fontWeight: 800 }}>
                      {((safeParse(appraisalData.managerRatingTask) * validBusinessWeight) / 100).toFixed(2)}
                    </div>
                  </div>
                )}
                {/* Manager's Business Comments */}
                <div style={{ width: '100%', padding: '0.5rem 1.25rem 0.75rem 1.25rem' }}>
                  {canManagerEdit ? (
                    <TextAreaField
                      label="Manager's Comments (Business Objectives)"
                      value={appraisalData.managerBusinessComments || ''}
                      onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerBusinessComments: value }))}
                      rows={2}
                      placeholder="Provide specific feedback on this employee's business objective performance..."
                      inputWidth="100%"
                    />
                  ) : (
                    appraisalData.managerBusinessComments && (
                      <div style={{ fontSize: '0.85rem', color: 'var(--apr-text-secondary)', fontStyle: 'italic', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        💬 <strong>Manager's Comments:</strong> {appraisalData.managerBusinessComments}
                      </div>
                    )
                  )}
                </div>
              </div>
            </CriteriaSection>
            </div>

            {/* ── DIVIDER ── */}
            <hr className="apr-subsection-divider" />

                        <div className="apr-subsection apr-subsection--leadership">
              <div className="apr-subsection-header">
                <h5>👔 Leadership & Behavioural Competencies</h5>
                <span className="apr-subsection-weight">{validLeadershipWeight}% Weight</span>
              </div>
              
              <CriteriaSection 
                description={`Evaluate leadership behaviors, values, and interpersonal skills`}
              >
                <div>
                  <div className="apr-score-header-row">
                    <div className="apr-score-cell-main">Competency</div>
                    <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>Rating</div>
                    <div className="apr-score-cell-weight">Weight</div>
                    <div className="apr-score-cell-result">Score</div>
                  </div>

                  {/* Leadership competency content - keep as is */}
                  {leadershipLoading ? (
                    <div className="apr-loading">
                      <CircularLoader small />
                      <span>Loading competencies...</span>
                    </div>
                  ) : (
                  <>
                    {(() => {
                      const isManagerView = isManager && !isEmployee;
                      return leadershipCompetencies.map((comp) => {
                        const currentRatings = isManagerView ? managerCompetencyRatings : competencyRatings;
                        const currentComments = isManagerView ? managerCompetencyComments : competencyComments;
                        const rating = currentRatings[comp.id] || '';
                        const comment = currentComments[comp.id] || '';
                        const score = rating ? ((parseFloat(rating) * (comp.weight / 100)) || 0).toFixed(2) : '0.00';
                        const canEdit = isManagerView ? canManagerEdit : canEmployeeEdit;
                        const isExpanded = expandedCompId === comp.id;

                    return (
                      <div key={comp.id} className="apr-competency-card">
                      {/* ---- Row 1: Name | Weight | Score ---- */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', width: '100%', alignItems: 'center', gap: '1rem' }}>
                        <div className="apr-score-cell-main">
                          <div className="apr-score-label">{comp.name}</div>
                          <div className="apr-score-hint">{comp.displayContent}</div>
                          {comp.behaviouralIndicators && Object.keys(comp.behaviouralIndicators).length > 0 && (
                            <button 
                              className="apr-indicator-btn"
                              onClick={() => setExpandedCompId(isExpanded ? null : comp.id)}
                              style={{ marginTop: '0.5rem' }}
                            >
                              {isExpanded ? 'Hide Indicators' : 'View Behavioural Indicators'}
                            </button>
                          )}
                        </div>
                        <div className="apr-score-cell-weight" style={{ textAlign: 'center', fontWeight: 600 }}>
                          {Math.round(comp.weight * 100)}%
                        </div>
                        <div className="apr-score-cell-result" style={{ textAlign: 'center', fontWeight: 800 }}>
                          {score}
                        </div>
                      </div>
                      
                      {isManagerView && (
                        <div style={{ 
                          width: '100%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '1rem', 
                          marginBottom: '0.25rem',
                          padding: '4px 8px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--apr-text-secondary)' }}>
                            Employee’s Self Rating:
                          </span>
                          <div style={{
                            padding: '2px 12px',
                            backgroundColor: competencyRatings[comp.id] ? leadershipRatingScale[competencyRatings[comp.id]]?.bgColor || '#f1f5f9' : '#f1f5f9',
                            borderRadius: '4px',
                            border: `1px solid ${competencyRatings[comp.id] ? leadershipRatingScale[competencyRatings[comp.id]]?.color || '#cbd5e1' : '#e2e8f0'}`,
                            fontWeight: 600,
                            color: competencyRatings[comp.id] ? leadershipRatingScale[competencyRatings[comp.id]]?.color : '#64748b',
                            fontSize: '0.85rem'
                          }}>
                            {competencyRatings[comp.id] ? `${competencyRatings[comp.id]} – ${leadershipRatingScale[competencyRatings[comp.id]]?.label}` : 'Not rated'}
                          </div>
                          {competencyComments[comp.id] && (
                            <span style={{ fontSize: '0.8rem', color: 'var(--apr-text-secondary)', fontStyle: 'italic' }}>
                              “{competencyComments[comp.id]}”
                            </span>
                          )}
                        </div>
                      )}
                     
                      {/* ---- Row 2: Rating Cards (full width) ---- */}
                      <div style={{ width: '100%', marginTop: '0.25rem' }}>
                        {canEdit ? (
                          <RatingCardSelector
                            options={Object.entries(leadershipRatingScale || RATING_DEFINITIONS)
                              .sort(([a], [b]) => Number(b) - Number(a))
                              .map(([key, def]) => ({ value: key, label: def.label }))}
                            selected={rating}
                            onChange={(newRating) => {
                              if (isManagerView) {
                                setManagerCompetencyRatings(prev => ({ ...prev, [comp.id]: newRating }));
                                const allRatings = { ...managerCompetencyRatings, [comp.id]: newRating };
                                const validRatings = Object.values(allRatings).filter(r => r !== '');
                                const avg = validRatings.length > 0 ? (validRatings.reduce((sum, r) => sum + parseFloat(r), 0) / validRatings.length).toFixed(1) : '';
                                setAppraisalData(prev => ({ ...prev, managerRatingBehavioural: avg }));
                              } else {
                                setCompetencyRatings(prev => ({ ...prev, [comp.id]: newRating }));
                                const allRatings = { ...competencyRatings, [comp.id]: newRating };
                                const validRatings = Object.values(allRatings).filter(r => r !== '');
                                const avg = validRatings.length > 0 ? (validRatings.reduce((sum, r) => sum + parseFloat(r), 0) / validRatings.length).toFixed(1) : '';
                                setAppraisalData(prev => ({ ...prev, employeeSelfRatingBehavioural: avg }));
                              }
                            }}
                            disabled={!canEdit}
                            ratingScale={leadershipRatingScale}
                          />
                        ) : (
                          <div style={{
                            padding: '6px 12px',
                            backgroundColor: rating ? leadershipRatingScale[rating]?.bgColor || '#f1f5f9' : '#f8f9fa',
                            borderRadius: '8px',
                            border: `1px solid ${rating ? leadershipRatingScale[rating]?.color || '#cbd5e1' : '#e2e8f0'}`,
                            fontWeight: 700,
                            color: rating ? leadershipRatingScale[rating]?.color : '#64748b',
                            display: 'inline-block'
                          }}>
                            {rating ? `${rating} - ${leadershipRatingScale[rating]?.label}` : '—'}
                          </div>
                        )}
                      </div>

                      {/* ---- Row 3: Comments (full width) ---- */}
                      <div style={{ width: '100%', marginTop: '0.25rem' }}>
                        {canEdit ? (
                          <TextAreaField
                            label="Comments"
                            value={comment}
                            onChange={({ value }) => {
                              if (isManagerView) setManagerCompetencyComments(prev => ({ ...prev, [comp.id]: value }));
                              else setCompetencyComments(prev => ({ ...prev, [comp.id]: value }));
                            }}
                            rows={2}
                            placeholder="Provide specific feedback..."
                            inputWidth="100%"
                          />
                        ) : (
                          comment && (
                            <div style={{ fontSize: '0.85rem', color: 'var(--apr-text-secondary)', fontStyle: 'italic', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                              💬 <strong>{isManagerView ? 'Manager:' : 'Self:'}</strong> {comment}
                            </div>
                          )
                        )}
                      </div>

                      {/* ---- Expanded Behavioural Indicators (when toggled) ---- */}
                      {isExpanded && comp.behaviouralIndicators && (
                        <div className="apr-indicators-list" style={{ width: '100%', marginTop: '0.5rem' }}>
                          {[5, 4, 3, 2, 1].map(level => {
                            const indicator = comp.behaviouralIndicators[level];
                            if (!indicator) return null;
                            return (
                              <div key={level} className={`apr-indicator-row apr-indicator-row--${level}`}>
                                <div className="apr-indicator-level">{level}</div>
                                <div>{indicator}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                        );
                      });
                    })()}

                    <div className="apr-score-total-row">
                      <div className="apr-score-cell-main">Overall Leadership Rating</div>
                      <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>
                        {isManager && !isEmployee ? appraisalData.managerRatingBehavioural : appraisalData.employeeSelfRatingBehavioural}
                      </div>
                      <div className="apr-score-cell-weight" style={{ textAlign: 'center' }}>{validLeadershipWeight}%</div>
                      <div className="apr-score-cell-result" style={{ textAlign: 'center' }}>
                        {((safeParse(isManager && !isEmployee ? appraisalData.managerRatingBehavioural : appraisalData.employeeSelfRatingBehavioural) * validLeadershipWeight) / 100).toFixed(2)}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CriteriaSection>

            {/* Total Performance Score Banner */}
            <div className="apr-total-banner">
              <div>
                <h4 style={{ margin: 0, color: '#1b5e20' }}>Total Performance Score</h4>
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', color: '#388e3c' }}>
                  Weighted result from Business ({validBusinessWeight}%) and Leadership ({validLeadershipWeight}%)
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="apr-total-value">
                  {((safeParse(appraisalData.employeeSelfRatingTask) * validBusinessWeight + 
                    safeParse(appraisalData.employeeSelfRatingBehavioural) * validLeadershipWeight) / 100).toFixed(2)}
                </div>
                <div style={{ fontWeight: 800, color: '#1b5e20', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {businessRatingScale[Math.round((safeParse(appraisalData.employeeSelfRatingTask) * validBusinessWeight + 
                    safeParse(appraisalData.employeeSelfRatingBehavioural) * validLeadershipWeight) / 100)]?.label || 'In Progress'}
                </div>
              </div>
            </div>

           
            {/* Comments Section */}
            <div className="apr-comments-section">
              {canEmployeeEdit ? (
                <TextAreaField
                  label="Self-Assessment Comments"
                  helpText="Provide specific examples and context for your ratings"
                  value={appraisalData.employeeSelfComments}
                  onChange={({ value }) => {
                    setAppraisalData(prev => ({ ...prev, employeeSelfComments: value }));
                  }}
                  rows={4}
                  inputWidth="100%"
                  disabled={!canEmployeeEdit}
                />
              ) : (
                <div>
                  <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>Self-Assessment Comments</label>
                  <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px', minHeight: '80px' }}>
                    {appraisalData.employeeSelfComments || 'No comments provided'}
                  </div>
                </div>
              )}
            </div>

            {/* Actions - Same as before */}
            {isEmployee && !readOnlyReport &&
              appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.DRAFT && (
                <div className="apr-actions-bar">
                  <button
                    className="apr-btn apr-btn--secondary"
                    style={{ width: '120px' }}
                    onClick={() => saveAppraisal(APPRAISAL_STATUS_CODES.DRAFT)}>
                    Save Draft
                  </button>
                  <button
                    className="apr-btn apr-btn--primary"
                    style={{ minWidth: isTopLevelUser ? '220px' : '180px' }}
                    onClick={handleSubmit}>
                    {isTopLevelUser ? 'Submit & Auto-Approve' : 'Submit for Review'}
                  </button>
                </div>
              )}

            {/* Show message when in submitted status */}
            {isEmployee && appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED && (
              <div className="apr-actions-bar">
                <div style={{ padding: '8px 16px', backgroundColor: '#e3f2fd', borderRadius: '4px', width: '100%', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: '#1565c0' }}>
                    ⏳ Your appraisal has been submitted and is awaiting manager review. You cannot make changes at this time.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

      {/* Employee Response Summary - Show after finalization */}
      {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED && employeeResponseStatus && (
        <EmployeeResponseSummary
          responseStatus={employeeResponseStatus}
          responseRemarks={employeeResponseRemarks}
          acceptedTaskRating={employeeAcceptedRatingTask}
          acceptedBehaviouralRating={employeeAcceptedRatingBehavioural}
          managerRatingTask={appraisalData.managerRatingTask}
          managerRatingBehavioural={appraisalData.managerRatingBehavioural}
        />
      )}

      {/* ── No Appraisal Yet (Manager View) ─────────────────── */}
      {isManager && !appraisalData.eventId && (
        <div style={{ marginBottom: '1rem' }}>
          <NoticeBox warning title="No appraisal submitted yet">
            This employee has not submitted an appraisal for the {isMidYear ? 'Mid-Year' : 'Year-End'} review period.
            Please ask them to complete and submit their self-appraisal first.
            <div style={{ marginTop: '0.75rem' }}>
              <Button small secondary onClick={() => window.location.reload()}>
                Refresh
              </Button>
            </div>
          </NoticeBox>
        </div>
      )}

      {/* ── Manager / Evaluator Appraisal ───────────────────── */}
      {isManager && appraisalData.eventId && (
        <div className="apr-section">
          <div className="apr-card">
            <div className="apr-card-header">
              <div className="apr-card-header-left">
                <span className="apr-card-icon"><Users size={20} /></span>
                <h4 className="apr-card-title">
                  {isManager ? "Evaluator's Final Appraisal" : "Manager Feedback Review"}
                </h4>
              </div>
              <StatusChip status={appraisalData.appraisalStatus} />
            </div>

            <div className="apr-card-body" style={{ padding: '1.25rem 1.5rem' }}>
              {/* Table Header */}
              <div className="apr-score-header-row">
                <div className="apr-score-cell-main">Evaluation Criteria</div>
                <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>Recommended Rating</div>
                <div className="apr-score-cell-weight">Weight</div>
                <div className="apr-score-cell-result">Score</div>
              </div>

              {/* Business Objectives */}
              <div className="apr-score-row">
                <div className="apr-score-cell-main">
                  <div className="apr-score-label" style={{ color: 'var(--apr-primary)' }}>Business Objectives</div>
                  <div className="apr-score-hint">Performance against SMART objectives and KPIs</div>
                </div>
                <div className="apr-score-cell-rating">
                  <InputField
                    disabled={!canManagerEdit}
                    type="number"
                    value={String(appraisalData.managerRatingTask ?? '')}
                    onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerRatingTask: value }))}
                    min="0"
                    max="5"
                    step="1"
                    inputWidth="100%"
                    placeholder="0-5"
                  />
                </div>
                <div className="apr-score-cell-weight" style={{ fontWeight: 600 }}>50%</div>
                <div className="apr-score-cell-result" style={{ fontWeight: 800 }}>
                  {calculateTotalScore(appraisalData.managerRatingTask, '')}
                </div>
              </div>

              {/* Behavioural */}
              <div className="apr-score-row">
                <div className="apr-score-cell-main">
                  <div className="apr-score-label" style={{ color: 'var(--apr-primary)' }}>Leadership & Values</div>
                  <div className="apr-score-hint">Evaluation of core competencies and professional behavior</div>
                </div>
                <div className="apr-score-cell-rating">
                  <InputField
                    disabled={!canManagerEdit}
                    type="number"
                    value={String(appraisalData.managerRatingBehavioural ?? '')}
                    onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerRatingBehavioural: value }))}
                    min="0"
                    max="5"
                    step="1"
                    inputWidth="100%"
                    placeholder="0-5"
                  />
                </div>
                <div className="apr-score-cell-weight" style={{ fontWeight: 600 }}>50%</div>
                <div className="apr-score-cell-result" style={{ fontWeight: 800 }}>
                  {calculateTotalScore('', appraisalData.managerRatingBehavioural)}
                </div>
              </div>

              {/* Total Score for Manager */}
              <div className="apr-score-total-row" style={{ background: 'var(--apr-primary-light)', border: 'none' }}>
                <div className="apr-score-cell-main" style={{ color: 'var(--apr-primary-dark)' }}>Weighted Total Evaluation Score</div>
                <div className="apr-score-cell-rating" style={{ textAlign: 'center', fontSize: '1.25rem', color: 'var(--apr-primary-dark)' }}>
                  {computed.managerTotal}
                </div>
                <div className="apr-score-cell-weight">100%</div>
                <div className="apr-score-cell-result" style={{ fontSize: '1.25rem', color: 'var(--apr-primary-dark)' }}>
                  {calculateTotalScore(appraisalData.managerRatingTask, appraisalData.managerRatingBehavioural)}
                </div>
              </div>

              {/* Manager Comments */}
              <div style={{ marginTop: '2rem' }}>
                <TextAreaField
                  label="Manager's Narrative Feedback"
                  value={appraisalData.managerComments}
                  rows={4}
                  inputWidth="100%"
                  disabled={!canManagerEdit}
                  onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerComments: value }))}
                  placeholder="Provide detailed feedback on performance and development areas..."
                />
              </div>

              {/* Manager Actions */}
              {isManager && !readOnlyReport && (
                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
                  {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED && (
                    <button
                      className="apr-btn apr-btn--primary"
                      style={{ minWidth: '240px' }}
                      onClick={handleManagerReview}
                      disabled={!appraisalData.managerRatingTask || !appraisalData.managerRatingBehavioural}
                    >
                      Submit Final Manager Review
                    </button>
                  )}
                  {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.MANAGER_REVIEWED && (
                    <div style={{ width: '100%' }}>
                      <NoticeBox info title="Review Submitted">
                        Your evaluation has been submitted. Awaiting employee acknowledgement and finalization.
                      </NoticeBox>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Employee Response Section - Shown when manager has reviewed */}
      {isEmployee && !readOnlyReport &&
        appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.MANAGER_REVIEWED &&
        !isFinalizedAndLocked && (
          <EmployeeResponseSection
            onFinalize={handleFinalizeAfterReview}
            disabled={loading}
            managerRatingTask={appraisalData.managerRatingTask}
            managerRatingBehavioural={appraisalData.managerRatingBehavioural}
            managerComments={appraisalData.managerComments}
            initialRemarks={employeeResponseRemarks}
          />
        )}

      {/* Employee Finalization - now combined into the EmployeeResponseSection */}

      {/* ── Status Footer ────────────────────────────────────── */}
      <div className="apr-status-footer">
        <span className="apr-status-footer-label">Current Status: </span>
        <StatusChip status={appraisalData.appraisalStatus} />
        {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED && (
          <span style={{ color: 'var(--apr-success)', display: 'flex', alignItems: 'center' }}>
            <Lock size={14} style={{ marginRight: 4 }} /> Locked
          </span>
        )}
      </div>
    </div>
  );
};

export default AppraisalForm;
